import React, { useEffect, useState, useCallback } from "react";
import "../../../../Modal/dialog.css";

/**
 * Shared modal: pre-call setup (Save & Continue) and edit saved values (Update).
 */
function TestCallVariablesModal({
  open,
  mode,
  variableNames,
  initialValues,
  onSave,
  onCancel,
  validationError,
  onFieldChange,
}) {
  const [values, setValues] = useState({});
  const [invalidNames, setInvalidNames] = useState(() => new Set());

  const names = variableNames || [];
  const hasNames = names.length > 0;
  const isEdit = mode === "edit";
  const modalTitle = isEdit ? "Edit Test Variables" : "Test Call Setup";

  useEffect(() => {
    if (!open) return;
    setInvalidNames(new Set());
    const list = variableNames || [];
    if (list.length === 0) {
      setValues({});
      return;
    }
    const next = {};
    list.forEach((name) => {
      next[name] =
        initialValues && initialValues[name] != null ? String(initialValues[name]) : "";
    });
    setValues(next);
  }, [open, mode, variableNames, initialValues]);

  const setupIntro =
    hasNames && !isEdit ? (
      <>
        <p style={{ marginBottom: "0.35rem", color: "#3f4254", fontWeight: 500 }}>
          We’ve detected variables in your workspace.
        </p>
        <p style={{ marginBottom: "1rem", color: "#646c9a" }}>
          Please provide values to test with.
        </p>
      </>
    ) : null;

  const primaryLabel = isEdit ? "Update" : "Save & Continue";

  const handleChange = useCallback(
    (name, v) => {
      setValues((prev) => ({ ...prev, [name]: v }));
      setInvalidNames((prev) => {
        if (!prev.has(name)) return prev;
        const next = new Set(prev);
        next.delete(name);
        return next;
      });
      if (typeof onFieldChange === "function") onFieldChange();
    },
    [onFieldChange]
  );

  const handlePrimary = () => {
    if (!hasNames) return;
    const empty = names.filter((n) => !String(values[n] ?? "").trim());
    if (empty.length) {
      setInvalidNames(new Set(empty));
      return;
    }
    setInvalidNames(new Set());
    onSave(values);
  };

  const showInlineError =
    (validationError && String(validationError).trim()) || invalidNames.size > 0;

  const errorText =
    validationError && String(validationError).trim()
      ? validationError
      : "Some variables are missing values";

  if (!open) return null;

  return (
    <div
      className="modal completedchats fade show"
      data-backdrop="static"
      tabIndex="-1"
      role="dialog"
      data-keyboard="false"
      aria-labelledby="testCallVariablesModalTitle"
      aria-hidden="false"
      style={{ display: "block", zIndex: 100050 }}
    >
      <div
        className="modal-dialog modal-dialog-centered"
        role="document"
        style={{ maxWidth: hasNames ? 700 : 600 }}
      >
        <div className="modal-content test-call-variables-modal">
          <button
            type="button"
            className="close linear-gradient"
            data-dismiss="modal"
            aria-label="Close"
            onClick={onCancel}
            style={{ zIndex: 100051 }}
          >
            X
          </button>
          <div className="modal-header test-call-variables-modal__header">
            <h5
              className="modal-title test-call-variables-modal__title"
              id="testCallVariablesModalTitle"
            >
              {modalTitle}
            </h5>
          </div>
          <div className="modal-body parsedatap viewemail" style={{ paddingTop: "10px" }}>
            <div style={{ marginLeft: "-5px" }}>
              {setupIntro}

              {!hasNames ? (
                <div
                  style={{
                    margin: "1.25rem 0 0.5rem",
                    padding: "1.75rem 1.5rem",
                    textAlign: "center",
                    borderRadius: "8px",
                    background: "#f5f8fa",
                    border: "1px solid #e4e6ef",
                  }}
                >
                  <p
                    style={{
                      margin: 0,
                      fontSize: "1.2rem",
                      color: "#3f4254",
                      lineHeight: 1.55,
                    }}
                  >
                    No{" "}
                    <code
                      style={{
                        fontSize: "1em",
                        padding: "3px 8px",
                        borderRadius: "4px",
                        background: "#fff",
                        border: "1px solid #e4e6ef",
                        color: "#3699ff",
                      }}
                    >
                      {"{{variables}}"}
                    </code>{" "}
                    were detected for this workspace yet.
                  </p>
                </div>
              ) : null}

              {showInlineError ? (
                <div className="alert alert-danger" style={{ marginBottom: "1rem" }}>
                  {errorText}
                </div>
              ) : null}

              {hasNames ? (
                <div className="table-responsive" style={{ marginTop: "0.5rem" }}>
                  <table className="table table-bordered table-sm mb-0">
                    <thead className="thead-light">
                      <tr>
                        <th scope="col" style={{ width: "38%" }}>
                          Variable Name
                        </th>
                        <th scope="col">Test Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {names.map((name) => {
                        const invalid = invalidNames.has(name);
                        return (
                          <tr key={name} className={invalid ? "table-danger" : undefined}>
                            <td className="align-middle">
                              <code className="test-call-variables-modal__var-name">{name}</code>
                            </td>
                            <td>
                              <input
                                type="text"
                                id={`test_call_var_${name}`}
                                className={`form-control ${invalid ? "is-invalid" : ""}`}
                                name={`test_var_${name}`}
                                value={values[name] != null ? values[name] : ""}
                                onChange={(e) => handleChange(name, e.target.value)}
                                placeholder="__________"
                                autoComplete="off"
                                aria-invalid={invalid}
                                style={{ borderRadius: "4px" }}
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : null}
            </div>
          </div>
          <div
            className="modal-footer test-call-variables-modal__footer"
            style={{ display: "flex", justifyContent: "flex-end", gap: "8px", flexWrap: "wrap" }}
          >
            {hasNames ? (
              <>
                <button
                  type="button"
                  className="yr-cancelbtn linear-gradient-cancel btn-sm yr-btn-small"
                  onClick={onCancel}
                  data-dismiss="modal"
                >
                  <span>Cancel</span>
                </button>
                <button
                  type="button"
                  className="btn linear-gradient yr-submitbtn btn-sm"
                  onClick={handlePrimary}
                >
                  <span>{primaryLabel}</span>
                </button>
              </>
            ) : (
              <button
                type="button"
                className="yr-cancelbtn linear-gradient-cancel btn-sm yr-btn-small"
                onClick={onCancel}
                data-dismiss="modal"
              >
                <span>Close</span>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default TestCallVariablesModal;
